import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { appDateTime } from '@/utils/appDateFormat';
import { Download, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/Card';
import Button from '../../../../components/ui/Button';
import StatusBadge from '../../../../components/ui/StatusBadge';
import {
    DataExportRequestView,
    downloadDataExport,
    listDataExports,
    requestEmployeeDataExport,
    requestMyDataExport,
} from '../../../../api/General/dataExport';
import { parseApiUtc } from '@/utils/apiDate';

// CO-326 — "Download my data" (self) and "Export this employee's data" (HR viewing an employee).
// Layout mirrors TermsAcceptanceSection so the Personal tab reads as one family of cards.

interface Props {
    userId: string;
    mode: 'self' | 'hr';
}

const POLL_MS = 4000;

const statusVariant: Record<string, 'success' | 'error' | 'warning' | 'info' | 'neutral'> = {
    Pending: 'info',
    Running: 'info',
    Ready: 'success',
    Failed: 'error',
    Expired: 'neutral',
};

function formatBytes(bytes?: number | null): string {
    if (bytes == null) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const DataExportSection: React.FC<Props> = ({ userId, mode }) => {
    const { t } = useTranslation();
    const [latest, setLatest] = useState<DataExportRequestView | null>(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const mountedRef = useRef(true);

    const load = useCallback(async () => {
        try {
            const res = await listDataExports(userId);
            if (!mountedRef.current) return;
            if (res.errors?.length) {
                setError(res.errors[0]?.description ?? t('dataExport.loadFailed'));
                return;
            }
            setLatest(res.payload?.[0] ?? null);
        } catch {
            if (mountedRef.current) setError(t('dataExport.loadFailed'));
        } finally {
            if (mountedRef.current) setLoading(false);
        }
    }, [userId, t]);

    useEffect(() => {
        mountedRef.current = true;
        setLoading(true);
        setError(null);
        load();
        return () => {
            mountedRef.current = false;
            if (pollRef.current) clearTimeout(pollRef.current);
        };
    }, [load]);

    const inProgress = latest?.status === 'Pending' || latest?.status === 'Running';

    // Poll only while an export is being prepared, and only while this card is mounted.
    useEffect(() => {
        if (!inProgress) return;
        pollRef.current = setTimeout(() => { load(); }, POLL_MS);
        return () => { if (pollRef.current) clearTimeout(pollRef.current); };
    }, [inProgress, latest, load]);

    const onRequest = async () => {
        setBusy(true);
        setError(null);
        try {
            const res = mode === 'self' ? await requestMyDataExport() : await requestEmployeeDataExport(userId);
            if (res.errors?.length) {
                setError(res.status === 409 ? t('dataExport.alreadyActive') : (res.errors[0]?.description ?? t('dataExport.requestFailed')));
                await load();
                return;
            }
            setLatest(res.payload);
        } catch (e: unknown) {
            const err = e as { response?: { status?: number }; __httpStatus?: number } | undefined;
            const status = err?.response?.status ?? err?.__httpStatus;
            setError(status === 409 ? t('dataExport.alreadyActive') : t('dataExport.requestFailed'));
            await load();
        } finally {
            setBusy(false);
        }
    };

    const onDownload = async () => {
        if (!latest) return;
        setDownloading(true);
        setError(null);
        try {
            await downloadDataExport(latest.id);
        } catch {
            setError(t('dataExport.downloadFailed'));
            await load();
        } finally {
            setDownloading(false);
        }
    };

    const title = mode === 'self' ? t('dataExport.titleSelf') : t('dataExport.titleHr');
    const description = mode === 'self' ? t('dataExport.descSelf') : t('dataExport.descHr');

    return (
        <Card data-testid={`data-export-card-${mode}`}>
            <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:justify-between">
                    <div className="flex items-start gap-3 min-w-0">
                        <div className="w-10 h-10 shrink-0 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Download className="w-5 h-5 text-primary" aria-hidden="true" />
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-lg font-semibold">{title}</h2>
                            <p className="text-sm text-muted-foreground">{description}</p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2 sm:justify-end shrink-0">
                        {latest?.canDownload && (
                            <Button
                                size="sm"
                                iconName="Download"
                                loading={downloading}
                                onClick={onDownload}
                                data-testid="data-export-download"
                            >
                                {t('dataExport.download')}
                            </Button>
                        )}
                        <Button
                            size="sm"
                            variant={latest?.canDownload ? 'outline' : 'default'}
                            loading={busy}
                            disabled={loading || inProgress}
                            onClick={onRequest}
                            data-testid="data-export-request"
                        >
                            {latest ? t('dataExport.requestAgain') : t('dataExport.request')}
                        </Button>
                    </div>
                </div>

                <div className="mt-4" aria-live="polite">
                    {loading && (
                        <div className="text-sm text-muted-foreground" data-testid="data-export-loading">
                            {t('Loading…')}
                        </div>
                    )}

                    {!loading && !latest && !error && (
                        <p className="text-sm text-muted-foreground" data-testid="data-export-none">
                            {t('dataExport.none')}
                        </p>
                    )}

                    {!loading && latest && (
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm" data-testid="data-export-latest">
                            <StatusBadge
                                variant={statusVariant[latest.status] ?? 'neutral'}
                                size="sm"
                                data-testid="data-export-status"
                                data-status={latest.status}
                            >
                                {inProgress && <Loader2 className="w-3 h-3 me-1 inline animate-spin" aria-hidden="true" />}
                                {t(`dataExport.status.${latest.status}`)}
                            </StatusBadge>
                            <span className="text-muted-foreground">
                                {t('dataExport.requestedOn', { date: appDateTime(parseApiUtc(latest.requestedUtc)) })}
                            </span>
                            {mode === 'self' && !latest.requestedBySubject && (
                                <span className="text-muted-foreground">{t('dataExport.requestedByHr')}</span>
                            )}
                            {latest.status === 'Ready' && latest.sizeBytes != null && (
                                <span className="text-muted-foreground" data-testid="data-export-size">
                                    {t('dataExport.size')}: <span dir="ltr">{formatBytes(latest.sizeBytes)}</span>
                                </span>
                            )}
                            {latest.canDownload && latest.expiresUtc && (
                                <span className="text-muted-foreground">
                                    {t('dataExport.expiresOn', { date: appDateTime(parseApiUtc(latest.expiresUtc)) })}
                                </span>
                            )}
                        </div>
                    )}

                    {!loading && inProgress && (
                        <p className="mt-2 text-sm text-muted-foreground">{t('dataExport.preparing')}</p>
                    )}
                    {!loading && latest?.status === 'Failed' && (
                        <p className="mt-2 text-sm text-rose-700">{t('dataExport.failedNote')}</p>
                    )}
                    {!loading && latest?.canDownload && (
                        <p className="mt-2 text-xs text-muted-foreground">{t('dataExport.linkNote')}</p>
                    )}

                    {error && (
                        <div className="mt-3 p-3 rounded-md text-sm bg-rose-50 text-rose-700 border border-rose-200" role="alert" data-testid="data-export-error">
                            {error}
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

export default DataExportSection;
